// import { canUseDOM } from 'exenv';
import type { CheckoutSite, Order } from '@trmediaab/zebra-types/trhub';
import type { Prettify } from '@trmediaab/zebra-types/utils';
import { useEffect, useMemo } from 'react';
import { proxy, useSnapshot } from 'valtio';
import { devtools } from 'valtio/utils';

import type { ErrorWithMessage } from '../types/ErrorWithMessage';

export type Step =
  | 'idle'
  | 'product'
  | 'payment'
  | 'user'
  | 'campaign'
  | 'redeem'
  | 'done';

interface MutableCheckoutState {
  appMode: 'redeemGift' | 'purchase';
  site: CheckoutSite;
  step: Step;
  order: Order | undefined;
  error: ErrorWithMessage | null;
  selectedSku: string | null;
  isCampaign: boolean;
  // Whether user can change to another product
  hasProductOptions: boolean | 'pending';
  redeemCode: string | null;
  // When the checkout is embedded a returnUrl parameter can be
  // set to redirect the user after successful checkout
  returnUrl?: string;
  // Overrides the default text on the return button
  returnUrlText?: string;
  // Analytics event data to attach to events sent from within the checkout
  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm
  extraAnalytics?: Record<string, unknown>;
  // In some contexts changing to another user cannot be allowed.
  // E.g on Joker Norway when the checkout is displayed inside a
  // user's bet. If the user changes it's no longer their bet.
  disallowUserChange: boolean;
}

let state: MutableCheckoutState;

interface InitialStateBase
  extends Pick<MutableCheckoutState, 'site' | 'returnUrl' | 'returnUrlText'> {
  analytics?: MutableCheckoutState['extraAnalytics'];
}

export interface InitialStateWithSku extends InitialStateBase {
  mode?: 'purchase';
  sku: string;
  disallowUserChange?: MutableCheckoutState['disallowUserChange'];
}

export interface InitialStateWithCampaign extends InitialStateBase {
  mode?: 'purchase';
  campaign: string;
  disallowUserChange?: MutableCheckoutState['disallowUserChange'];
}

export interface InitialStateWithRedeem extends InitialStateBase {
  mode: 'redeemGift';
}

type InitialStateData = Prettify<
  InitialStateWithSku | InitialStateWithCampaign | InitialStateWithRedeem
>;

export function useCreateState(data: InitialStateData) {
  const {
    returnUrl,
    returnUrlText,
    analytics: extraAnalytics,
    site,
    mode,
  } = data;

  const selectedSku = 'sku' in data && data.sku != null ? data.sku : null;
  const isCampaign = 'campaign' in data && data.campaign != null;
  const disallowUserChange =
    ('disallowUserChange' in data && data.disallowUserChange) ?? false;

  useMemo(() => {
    state = proxy({
      appMode: mode || 'purchase',
      site,
      step: 'idle',
      order: undefined,
      error: null,
      // Initially selected sku if provided
      selectedSku,
      isCampaign,
      hasProductOptions:
        isCampaign || mode === 'redeemGift' ? false : 'pending',
      // Gift redeem code if provided
      redeemCode: null,
      returnUrl,
      returnUrlText,
      extraAnalytics,
      disallowUserChange,
    });
  }, [
    disallowUserChange,
    extraAnalytics,
    isCampaign,
    mode,
    returnUrl,
    returnUrlText,
    selectedSku,
    site,
  ]);

  useEffect(() => {
    const unsub = devtools(state, { name: 'Checkout state' });
    return () => {
      unsub?.();
    };
  }, []);

  return useSnapshot(state);
}

function assertState() {
  if (state == null) {
    throw new Error('State has not been created');
  }
}

export const useImmutableState = () => {
  assertState();
  const snap = useSnapshot(state);
  return snap;
};

export type CheckoutState = ReturnType<typeof useImmutableState>;

export function useMutatableState() {
  assertState();
  return state;
}

export function setOrder(order: MutableCheckoutState['order']) {
  assertState();
  state.order = order;
}

export function setSelectedSku(sku: string) {
  assertState();
  state.selectedSku = sku;
}

export function setError(error: ErrorWithMessage) {
  assertState();
  state.error = error;
}

export function setCheckoutStep(step: MutableCheckoutState['step']) {
  assertState();
  state.step = step;
}

export function setQuantity(quantity: number, index: number) {
  assertState();
  if (state.order == null) {
    throw new Error('Cannot set quantity on null order');
  }
  if (state.order.lineItems[index] == null) {
    throw new Error(`Cannot set quantity, no line item at index ${index}`);
  }

  state.order.lineItems[index].quantity = quantity;
}

export function setCountry(country: string) {
  assertState();
  if (state.order == null) {
    throw new Error('Cannot set country on null order');
  }
  state.order.address = state.order.address || {};
  state.order.address.country = country;
}

export function setHasProductOptions(value: boolean | 'pending') {
  assertState();
  state.hasProductOptions = value;
}

export function setRedeemCode(code: string) {
  assertState();
  state.redeemCode = code;
}
